import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Truck, ExternalLink, ShieldCheck, PenLine, MapPin, XCircle } from "lucide-react";
import { fmtFecha, fmtFechaHora } from "@/lib/fechas";
import { fmtNumeroDeca, palabraEnvio } from "@/lib/textos";

export default function PublicRutaPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/ruta/${token}`);
        setData(data);
      } catch (e) { setErr(e.response?.data?.detail || "Ruta no disponible"); }
    })();
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="p-6 border-red-200 bg-red-50 max-w-md">
        <XCircle className="h-6 w-6 text-red-700"/>
        <div className="mt-1 font-semibold text-red-900">{err}</div>
      </Card>
    </div>
  );
  if (!data) return <div className="p-8 text-center text-slate-500">Cargando…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-blue-700 flex items-center justify-center"><Truck className="h-5 w-5 text-white"/></div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Ruta compartida</div>
            <div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>{data.nombre}</div>
            <div className="text-xs text-slate-500 mt-0.5">{data.num_decas} DeCA · válida hasta {fmtFechaHora(data.expires_at)}</div>
          </div>
        </div>

        <div className="space-y-3">
          {data.decas.map((d) => (
            <Card key={d.id} className={`p-4 ${d.revocado ? "border-red-200" : ""}`} data-testid={`ruta-deca-${d.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="label-tag">{fmtNumeroDeca(d)}</div>
                  <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>{d.cargador} <span className="text-slate-400">→</span> {d.transportista}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtFecha(d.fecha_transporte)} · {(d.matriculas||[]).join(", ") || "sin matrícula"} · v{d.version}</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-700" style={{ fontFamily: "Chivo" }}>{d.num_envios}</div>
                  <div className="label-tag">{palabraEnvio(d.num_envios)}</div>
                </div>
              </div>
              <div className="mt-3 grid gap-1.5">
                {d.envios.map((e) => (
                  <div key={e.orden} className="flex items-start gap-2 text-sm border-l-2 border-blue-200 pl-2">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-700 truncate"><b>{e.destinatario}</b> — {e.destino}</div>
                      <div className="text-xs text-slate-500 truncate">Desde: {e.origen} · {e.bultos ?? "—"} bultos · {e.peso_bruto ?? 0} kg</div>
                    </div>
                    {e.firmado_at ? (
                      <span className="text-[10px] text-emerald-700 shrink-0">Firmado</span>
                    ) : e.firma_token && !d.revocado ? (
                      <a href={`/firmar/${e.firma_token}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="shrink-0 h-8"><PenLine className="h-3.5 w-3.5 mr-1"/> Firmar</Button>
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href={d.pdf_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" disabled={d.revocado}><ExternalLink className="h-3.5 w-3.5 mr-1.5"/> PDF</Button></a>
                <a href={d.verify_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ShieldCheck className="h-3.5 w-3.5 mr-1.5"/> Verificar</Button></a>
              </div>
            </Card>
          ))}
        </div>
        <div className="mt-6 text-[11px] text-slate-500 text-center">Enlace temporal · Resolución 5/6/2026 · Verificación e2e</div>
      </div>
    </div>
  );
}
